"use client";

import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Shield } from "lucide-react"; // or replace with your preferred icon

const stats = [
  { number: 3, suffix: "+", label: "Years of Difference Making" },
  { number: 1000, suffix: "+", label: "Units Sold" },
  { number: 700, suffix: "+", label: "Channel Partner Network" },
  { number: 25, suffix: "+", label: "Satisfied Developers" },
  { number: 40, suffix: "+", label: "Experts" },
  { number: 700, suffix: "Cr+", label: "Inventory Sold", isCurrency: true },
];

const Counter = ({ value, suffix = "", isCurrency = false }) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!inView) return;

    let startTimestamp: number | null = null;
    const duration = 1500;

    const animate = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * value));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };

    requestAnimationFrame(animate);
  }, [inView, value]);

  return (
    <span
      ref={ref}
      className="text-[#741112] text-2xl md:text-4xl font-extrabold"
    >
      {isCurrency ? "₹ " : ""}
      {count}
      {suffix}
    </span>
  );
};

const StatsSection = () => {
  return (
    <section className="bg-white py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-4">

        {/* Intro section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center bg-[#741112]/10 rounded-full px-3 py-1 mb-3">
            <Shield className="h-4 w-4 text-[#741112] mr-2" />
            <span className="text-[#741112] font-medium text-sm">Milestone</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#741112]">
            Our Milestones
          </h2>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-8 text-center">
          {stats.map((item, index) => (
            <motion.div
              key={index}
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Counter
                value={item.number}
                suffix={item.suffix}
                isCurrency={item.isCurrency}
              />
              <span className="text-gray-700 text-sm md:text-base font-medium mt-2 leading-snug text-center">
                {item.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
